import { FilterCriteriaInputType } from '@zspace/types';
import { KeyboardEvent, useCallback, useEffect, useState } from 'react';
import { Columns, Element, Form } from 'react-bulma-components';
import FeedbackMessage from '../../../../ui/feedback-message/feedback-message';
import styles from './filter-criteria-input.module.scss';

type FilterItemFilterCriteriaInputType<T> = FilterCriteriaInputType<T> & {
  placeholder?: string;
};

export type FilterCriteriaInputProps<T> = {
  name: string;
  localData: T;
  setLocalData: (value: T) => void;
  onValidateField?: (fieldName: string, valid: boolean) => void;
  errorText?: string;
  input: FilterItemFilterCriteriaInputType<T>;
};

export function FilterCriteriaInput<T>({
  input: { valueKey, placeholder },
  name,
  errorText,
  localData,
  setLocalData,
}: FilterCriteriaInputProps<T>) {
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    const value = (localData[valueKey] as string[])?.join(', ');
    setInputValue(value ?? '');
  }, [localData, valueKey]);

  const handleInputChange = useCallback(
    (value: string) => {
      const values = value
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v.length > 0);

      setLocalData({
        ...localData,
        [valueKey]: values,
      });
    },
    [localData, setLocalData, valueKey]
  );

  const handleSearchOnKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>): void => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleInputChange(event.currentTarget.value);
      }
    },
    [handleInputChange]
  );

  return (
    <Element className={styles.filterCriteriaContainer}>
      <Columns marginless display="flex" className="gap-6">
        <Columns.Column paddingless>
          <Form.Field>
            <Form.Control>
              <Form.Textarea
                id={name}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleSearchOnKeyDown}
                onBlur={(e) => handleInputChange(e.target.value)}
                placeholder={placeholder}
                rows={4}
                className={styles.textarea}
              />
            </Form.Control>
          </Form.Field>
        </Columns.Column>
      </Columns>
      <Element mt={2}>
        <span className="is-size-7">
          You can input as many comma separated values as needed
        </span>
      </Element>
      <FeedbackMessage>{errorText}</FeedbackMessage>
    </Element>
  );
}

export default FilterCriteriaInput;
